// Immutable bundled catalog version. New catalog releases must keep old version modules available.
import italy from './versions/2026-11-03/italy.json' with {type:'json'};
import japan from './versions/2026-11-03/japan.json' with {type:'json'};
import type {Recipe,Step,IngredientLine,Diet} from '../src/types.ts';
import type {SeedUnitKey} from '../src/logic/units.ts';
export const CANONICAL_VERSION='2026-11-03';
const fromNumber=(n:number)=>{let value=Math.round(n*1000),places=3;while(places>0&&value%10===0){value/=10;places--;}return {value,places};};
type RawLine = { ingredient: string; amount: number; unit: string | null };
type RawUtensil = { utensil: string; amount: number; scaleEvery?: number };
type RawStep = { title: string; explainer?: string; uses?: string[]; timers?: { label: string; minutes: number }[] };
type RawRecipe = {
  id: string; title: string; explainer: string; servings?: number; durationMin?: number; notDiet?: string[];
  ingredients: RawLine[]; utensils: RawUtensil[]; steps: RawStep[];
};
export type RawCookbook = {
  id: string; country: string; flag: string; title: string; explainer: string; premium: boolean;
  sections: { title: string; note: string; holiday?: string; recipes: RawRecipe[] }[];
};

export const RAW_COOKBOOKS = [italy,japan] as RawCookbook[];

const toLine = (l: RawLine): IngredientLine => ({
  ingredientId: l.ingredient,
  amount: fromNumber(l.amount),
  unit: l.unit ? { seed: l.unit as SeedUnitKey } : null,
});

function toRecipe(r: RawRecipe): Recipe {
  const byIngredient = new Map(r.ingredients.map(l => [l.ingredient, toLine(l)]));
  const steps: Step[] = r.steps.map((s, i) => ({
    id: `${r.id}#${i + 1}`,
    title: s.title,
    explainer: s.explainer ?? '',
    ingredients: (s.uses ?? []).map(u => byIngredient.get(u)).filter((x): x is IngredientLine => !!x),
    utensils: [],
    timers: (s.timers ?? []).map(t => ({ label: t.label, seconds: Math.round(t.minutes * 60) })),
  }));
  return {
    id: r.id,
    title: r.title,
    explainer: r.explainer,
    servings: r.servings ?? 1,
    durationMin: r.durationMin ?? 0,
    priceCents: 0,
    ingredients: r.ingredients.map(toLine),
    utensils: r.utensils.map(u => ({ utensilId: u.utensil, amount: u.amount, scaleEvery: u.scaleEvery ?? null })),
    steps,
    ...(r.notDiet?.length ? { notDiet: r.notDiet as Diet[] } : {}),
    updatedAt: 0,
  };
}


export const CONTENT_RECIPES:Record<string,Recipe>=Object.fromEntries(RAW_COOKBOOKS.flatMap(c=>c.sections.flatMap(s=>s.recipes)).map(r=>[r.id,toRecipe(r)]));
export type RecipeReference={id:string;version:string;savedAt:number;overrides?:Record<string,unknown>};
export function resolveRecipe(ref:RecipeReference):Recipe|undefined{
  const base=ref.version===CANONICAL_VERSION?CONTENT_RECIPES[ref.id]:undefined;
  return base?{...base,...ref.overrides,id:ref.id,canonical:{id:ref.id,version:ref.version},updatedAt:ref.savedAt}:undefined;
}
export function portableLibrary(data:Record<string,unknown>):Record<string,unknown>{
  const recipes={...((data.recipes??{}) as Record<string,Recipe>)};
  const savedRecipes=Object.fromEntries(Object.entries((data.savedRecipes??{}) as Record<string,RecipeReference>).filter(([,ref])=>!resolveRecipe(ref))); // Preserve references newer than this client; removed known recipes stay removed.
  for(const [id,recipe]of Object.entries(recipes))if(recipe.canonical&&CONTENT_RECIPES[id]){
    const base=CONTENT_RECIPES[id] as unknown as Record<string,unknown>;
    const overrides=Object.fromEntries(Object.entries(recipe).filter(([key,value])=>!['id','canonical','updatedAt'].includes(key)&&JSON.stringify(value)!==JSON.stringify(base[key])));
    savedRecipes[id]={id,version:recipe.canonical.version,savedAt:recipe.updatedAt,...(Object.keys(overrides).length?{overrides}:{})};delete recipes[id];
  }
  return {...data,recipes,savedRecipes};
}
export function hydrateLibrary<T extends Record<string,unknown>>(data:T):T{
  const recipes={...((data.recipes??{}) as Record<string,Recipe>)};
  for(const ref of Object.values((data.savedRecipes??{}) as Record<string,RecipeReference>)){const recipe=resolveRecipe(ref);if(recipe&&!recipes[recipe.id])recipes[recipe.id]=recipe;}
  return {...data,recipes};
}
